<script setup lang="ts">
import { computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import {
  CheckCircleIcon,
  ClipboardDocumentCheckIcon,
  ExclamationTriangleIcon,
  FireIcon,
} from '@heroicons/vue/24/outline'
import { useActionItemStore, type ActionItem, type ActionPriority } from '@/stores/useActionItemStore'

const router = useRouter()
const actionStore = useActionItemStore()
const { items, count } = storeToRefs(actionStore)

onMounted(() => {
  actionStore.fetchActions()
  actionStore.fetchCount()
})

const preview = computed(() => items.value.slice(0, 3))

const PRIORITY_UI: Record<ActionPriority, { chip: string; icon: typeof FireIcon }> = {
  URGENT: { chip: 'bg-error/10 text-error', icon: FireIcon },
  HIGH: { chip: 'bg-warning/15 text-warning', icon: ExclamationTriangleIcon },
  NORMAL: { chip: 'bg-info/10 text-info', icon: CheckCircleIcon },
}
const priorityUi = (p: ActionPriority) => PRIORITY_UI[p] ?? PRIORITY_UI.NORMAL

function runAction(item: ActionItem) {
  const route = item.cta?.route
  if (!route) return
  router.push({ path: route, query: (item.cta?.params as Record<string, string>) ?? {} })
}
</script>

<template>
  <article class="card border border-base-300 bg-base-100 shadow-sm">
    <div class="card-body gap-3 p-4 md:p-5">
      <div class="flex items-center justify-between gap-2">
        <h2 class="card-title flex items-center gap-2 text-lg font-semibold">
          <ClipboardDocumentCheckIcon class="h-5 w-5 text-primary" aria-hidden="true" />
          À faire
          <span
            v-if="count.total"
            class="badge badge-sm"
            :class="count.urgent ? 'badge-error' : 'badge-warning'"
          >
            {{ count.total }}
          </span>
        </h2>
        <RouterLink to="/mes-actions" class="link link-hover text-sm text-base-content/60">
          Tout voir
        </RouterLink>
      </div>

      <div v-if="!count.total" class="flex items-center gap-2 py-2 text-base-content/70">
        <CheckCircleIcon class="h-5 w-5 text-success/70" aria-hidden="true" />
        Rien à traiter — vous êtes à jour.
      </div>

      <ul v-else class="flex flex-col gap-2">
        <li
          v-for="item in preview"
          :key="item.uuid"
          class="flex items-center gap-3 rounded-lg border border-base-200 p-2.5"
        >
          <span
            class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full"
            :class="priorityUi(item.effectivePriority).chip"
          >
            <component :is="priorityUi(item.effectivePriority).icon" class="h-4 w-4" aria-hidden="true" />
          </span>
          <span class="min-w-0 flex-1 truncate font-medium">{{ item.title }}</span>
          <button
            v-if="item.cta?.route"
            type="button"
            class="btn btn-primary btn-xs shrink-0"
            @click="runAction(item)"
          >
            {{ item.cta?.label || 'Traiter' }}
          </button>
        </li>
      </ul>

      <RouterLink
        v-if="count.total > preview.length"
        to="/mes-actions"
        class="btn btn-ghost btn-sm self-start"
      >
        Voir les {{ count.total }} actions
      </RouterLink>
    </div>
  </article>
</template>
